/**
 * Device-local record of "this browser already used its anonymous scan quota
 * for the current calendar month", shared by the analysis hook (writer) and the
 * pre-upload notice (reader).
 *
 * The stored value carries BOTH the quota month and WHY the flag was set:
 *
 *   `"2026-07:blocked"`  — an analyze attempt came back `signupRequired`; the
 *                          user is genuinely walled and the next attempt will
 *                          fail the same way.
 *   `"2026-07:consumed"` — an analyze attempt SUCCEEDED and used the last free
 *                          run. Nothing is walled yet, so this population must
 *                          not be shown the "you've used your free scan" notice
 *                          on that same visit.
 *
 * Values written before the reason existed are a bare month (`"2026-07"`) and
 * read as `blocked` — the pre-reason writer only ran on a truly-walled attempt,
 * so that is the conservative interpretation.
 *
 * Month-scoped on purpose: the server quota resets monthly, so a flag from a
 * previous month is stale and reads as not-exhausted with no cleanup pass.
 */

export type AnonQuotaReason = 'blocked' | 'consumed'

/** Calendar-month key ("2026-07") the flag is scoped to. */
export function currentQuotaMonth(): string {
  return new Date().toISOString().slice(0, 7)
}

function storageKey(mode: string): string {
  return `anonQuotaExhausted_${mode}`
}

/**
 * Reason the anon quota flag is set for `mode`, or null when it is absent or
 * belongs to an earlier month. Safe to call in any environment — a missing or
 * throwing localStorage degrades to null.
 */
export function readAnonQuotaReason(mode: string): AnonQuotaReason | null {
  if (typeof window === 'undefined') return null
  let stored: string | null = null
  try {
    stored = window.localStorage.getItem(storageKey(mode))
  } catch {
    return null /* localStorage unavailable */
  }
  if (!stored) return null

  const separator = stored.indexOf(':')
  const month = separator === -1 ? stored : stored.slice(0, separator)
  if (month !== currentQuotaMonth()) return null

  const reason = separator === -1 ? '' : stored.slice(separator + 1)
  return reason === 'consumed' ? 'consumed' : 'blocked'
}

export function writeAnonQuotaReason(mode: string, reason: AnonQuotaReason): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(storageKey(mode), `${currentQuotaMonth()}:${reason}`)
  } catch { /* localStorage unavailable */ }
}

export function clearAnonQuotaReason(mode: string): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.removeItem(storageKey(mode))
  } catch { /* localStorage unavailable */ }
}
